import { useRef, useEffect, useState, useMemo, ReactElement } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import {
  Row,
  LoadingSkeleton,
  Button,
  Drawer,
  Chip,
} from "@narmi/design_system";
import { catcat as cc, ContentCard, useSetChatVisibility } from "cerulean";
import { generateFrequencyString } from "byzantine";
import {
  getUntilEndString,
  showAmountStrikeThrough,
  getTotalAmount,
  DisplayStatus,
  useFetchPaymentMetadataById,
  formatPaymentActivity,
  useFetchPaymentActivityById,
  showCancelOption,
  getOriginalPaymentDirection,
} from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import {
  useDualApprovalPermission,
  useACHReverseCollectPermission,
  useACHReverseSendPermission,
} from "byzantine/src/dbbl/businessLogic/modules/achPayments/permissions";
import {
  useIsDomesticWireDualApprovalRequired,
  useIsInternationalWireDualApprovalRequired,
  useIsACHDualApprovalRequired,
  useUser,
} from "byzantine/src/dbbl/businessLogic/entities/users/hooks";
import {
  useAllRecipients,
  useFetchSwiftAdditionalDetails,
  useFetchSwiftAdditionalFieldsByCountry,
} from "byzantine/src/dbbl/businessLogic/entities/recipients";
import { useAllAccounts } from "byzantine/src/dbbl/businessLogic/entities";
import { useFetchBulkPaymentById } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import { useFetchBatch } from "byzantine/src/dbbl/businessLogic/entities/wireImport/requests";
import Filters from "byzantine/src/filters";
import utils from "byzantine/src/utils";
import { countryCodeToFlag } from "byzantine/src/dbbl/utils/internationalPhoneNumber";
import { FILTER_CONFIG, FilterChips } from "../Filters/filterConfig";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";
import parseTemplateName from "../common/templateNameUtils";
import styles from "../PaymentActivity.module.scss";
import { PAYMENT_ACTIVITY_ROUTE, getReverseRoute } from "../routes";
import { DrawerCard, DrawerHeader } from "../common";
import getPaymentTypeDrawerConfig from "./paymentTypeDrawerConfig";

export type PaymentMetadataProps = {
  entry_desc?: API.ACHPayment["entry_desc"];
  std_ent_cls_code?: API.ACHPayment["std_ent_cls_code"];
  description?: API.ACHPayment["description"];
  memo?: API.ACHPayment["memo"] | API.SwiftTransaction["memo"];
  note?: API.SwiftTransaction["note"];
  company_name?: API.ACHPayment["company_name"];
  company_id?: API.ACHPayment["company_id"];
  payment_identification?:
    | API.FedNowPayment["payment_identification"]
    | API.SwiftTransaction["payment_identification"];
  wire_reason?: API.Wire["wire_reason"];
  memo_1?: API.Wire["memo_1"];
  memo_2?: API.Wire["memo_2"];
  memo_3?: API.Wire["memo_3"];
  memo_4?: API.Wire["memo_4"];
  beneficiary_address_1?: API.Wire["beneficiary_address_1"];
  beneficiary_address_2?: API.Wire["beneficiary_address_2"];
  beneficiary_address_3?: API.Wire["beneficiary_address_3"];
  template_name?: API.SwiftTransaction["template_name"];
  quote?: API.FetchExchangeRateQuote.Response;
};

export type PaymentActivityDrawerProps = {
  isOpen: boolean;
  payment: API.FormattedPaymentActivity | null;
  handleClose: () => void;
  handleToggleCancelDialog?: (target: {
    id: string;
    model: string;
    usesDualApproval: boolean;
  }) => void;
  handleOnNext: () => void;
  handleOnPrev: () => void;
  paymentUuid: API.ScheduledACHPaymentId | API.ACHPaymentId;
  /** Hide all footer actions, e.g. on the reversal selection screen */
  hideFooterActions?: boolean;
};

const PaymentActivityDrawer = ({
  isOpen,
  payment,
  handleClose,
  handleToggleCancelDialog,
  handleOnNext,
  handleOnPrev,
  paymentUuid,
  hideFooterActions = false,
}: PaymentActivityDrawerProps) => {
  const drawerIdRef = useRef(
    `Drawer-${Math.random().toString(36).slice(2, 11)}`
  );
  useSetChatVisibility(drawerIdRef.current, !isOpen);
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const location = useLocation();
  const [paymentActivity, setPaymentActivity] =
    useState<API.FormattedPaymentActivity | null>(payment ?? null);
  const { send: fetchPaymentActivityById } = useFetchPaymentActivityById();
  const { send: fetchPaymentMetadata, loading } = useFetchPaymentMetadataById();
  const { send: fetchBulkPaymentById } = useFetchBulkPaymentById();
  const { send: fetchWireImportBatchById } = useFetchBatch();
  const { fetchSwiftAdditionalDetails } = useFetchSwiftAdditionalDetails();
  const { fetchSwiftAdditionalFieldsByCountry } =
    useFetchSwiftAdditionalFieldsByCountry();
  const isDomesticWireDualApprovalRequired =
    useIsDomesticWireDualApprovalRequired();
  const isInternationalWireDualApprovalRequired =
    useIsInternationalWireDualApprovalRequired();
  const isAchReversalDualApprovalRequired = useIsACHDualApprovalRequired();
  const currentUser = useUser();
  const [paymentMetadata, setPaymentMetadata] =
    useState<PaymentMetadataProps | null>(null);
  const [institutionDetails, setInstitutionDetails] = useState<{
    [key: string]: string;
  } | null>(null);
  const [renameFields, setRenameFields] = useState<{
    [key: string]: string;
  }>({});
  const [correspondentBankName, setCorrespondentBankName] = useState<
    string | null
  >(null);
  const [bulkTitle, setBulkTitle] = useState("");
  const [wireImportBatchTitle, setWireImportBatchTitle] = useState("");
  const recipients = useAllRecipients();
  const accounts = useAllAccounts();
  const hasDualApprovalPermission = useDualApprovalPermission();
  const hasReverseSendPermission = useACHReverseSendPermission();
  const hasReverseCollectPermission = useACHReverseCollectPermission();
  const institution = useInstitutionSettings();

  const drawerConfig = useMemo(
    () =>
      getPaymentTypeDrawerConfig({
        l10n,
        institutionDetails,
        renameFields,
        correspondentBankName,
      }),
    [l10n, institutionDetails, renameFields, correspondentBankName]
  );

  // if payment activity doesn't exist, fetch it
  useEffect(() => {
    if (!payment && paymentUuid) {
      fetchPaymentActivityById(paymentUuid)
        .then((response) => {
          const formattedData = formatPaymentActivity(
            response as API.PaymentActivity,
            recipients,
            accounts
          );
          setPaymentActivity(formattedData);
        })
        .catch((e) => console.error("Error fetching payment activity:", e));
    }
  }, [payment, paymentUuid, recipients, accounts]);

  useEffect(() => {
    if (payment) {
      setPaymentActivity(payment);
    }
  }, [payment]);

  useEffect(() => {
    const fetchSinglePaymentMetadata = async () => {
      // a reversal's company/SEC/entry details live on the original ACH
      // payment, so fetch the original payment's metadata (via the reversal's
      // `originalPayment` uuid)
      if (paymentActivity?.isReversal) {
        if (!paymentActivity?.originalPayment) return;
        try {
          const data = await fetchPaymentMetadata(
            paymentActivity.originalPayment,
            "UnverifiedACHTransaction"
          );
          setPaymentMetadata(data ?? null);
        } catch (error) {
          setPaymentMetadata(null);
        }
        return;
      }
      if (paymentActivity?.uuid && paymentActivity?.model) {
        try {
          const data = await fetchPaymentMetadata(
            paymentActivity?.uuid,
            paymentActivity?.model
          );
          setPaymentMetadata(data ?? null);
        } catch (error) {
          setPaymentMetadata(null);
        }
      }
    };

    const fetchBulkPaymentDetails = async () => {
      if (paymentActivity?.bulkPaymentId) {
        try {
          const data = await fetchBulkPaymentById(
            paymentActivity.bulkPaymentId
          );
          setBulkTitle(data?.title ?? "");
        } catch (error) {
          setBulkTitle("");
        }
      }
    };

    const fetchWireImportBatchDetails = async () => {
      if (paymentActivity?.wireImportBatchId) {
        try {
          const data = await fetchWireImportBatchById(
            paymentActivity.wireImportBatchId
          );
          setWireImportBatchTitle(data?.description ?? "");
        } catch (error) {
          setWireImportBatchTitle("");
        }
      }
    };

    setBulkTitle("");
    setWireImportBatchTitle("");
    fetchSinglePaymentMetadata();
    fetchBulkPaymentDetails();
    fetchWireImportBatchDetails();
  }, [paymentActivity]);

  useEffect(() => {
    const fetchAndSetAdditionalDetails = async (bic: string) => {
      const res = await fetchSwiftAdditionalDetails(bic);
      if (res?.additional_fields) {
        setInstitutionDetails({
          institutionName: res.institution_name,
        });
        setRenameFields(res.rename_fields);
      }
    };

    const swiftBic =
      paymentActivity?.destinationDetails?.bic ||
      paymentActivity?.recipient?.swift_destination?.bic;

    if (paymentActivity?.isInternationalWirePayment && swiftBic) {
      try {
        fetchAndSetAdditionalDetails(swiftBic);
      } catch (e) {
        setInstitutionDetails(null);
        setRenameFields({});
      }
    }
  }, [
    paymentActivity,
    paymentActivity?.isInternationalWirePayment,
    fetchSwiftAdditionalDetails,
  ]);

  // Fetch correspondent bank name and country-based rename fields
  useEffect(() => {
    const correspondentBic =
      paymentActivity?.recipient?.swift_destination?.correspondent_bank_bic;

    const fetchCorrespondentBankDetails = async (bic: string) => {
      const res = await fetchSwiftAdditionalDetails(bic);
      if (res?.institution_name) {
        setCorrespondentBankName(res.institution_name);
      }
      const countryCode =
        paymentActivity?.recipient?.swift_destination?.branch_address
          ?.country_code;
      if (countryCode) {
        const countryDetails = await fetchSwiftAdditionalFieldsByCountry(
          countryCode
        );
        if (countryDetails?.rename_fields) {
          setRenameFields(countryDetails.rename_fields);
        }
      }
    };

    if (paymentActivity?.isInternationalWirePayment && correspondentBic) {
      setCorrespondentBankName(null);
      fetchCorrespondentBankDetails(correspondentBic);
    }
  }, [
    paymentActivity,
    paymentActivity?.isInternationalWirePayment,
    fetchSwiftAdditionalDetails,
    fetchSwiftAdditionalFieldsByCountry,
  ]);

  if (!paymentActivity) return null;

  const {
    displayAmountDollars,
    displayStatus,
    recurrence,
    scheduledDate,
    frequency,
    rruleModel,
    amountDollars,
    parent,
    originalPayment,
    reversal,
    dualApprovalId,
    recipient,
    institutionAccount,
    uuid,
    startDate,
    isInstantPayment,
    isSameDayPayment,
    isCollectPayment,
    isFedWirePayment,
    isInternationalWirePayment,
    isReversal,
  } = paymentActivity;

  const showStrikeThrough = showAmountStrikeThrough(displayStatus);
  const showCancelButton =
    !hideFooterActions &&
    showCancelOption(paymentActivity, {
      currentUserUuid: currentUser?.id,
      isDomesticWireDualApprovalRequired,
      isInternationalWireDualApprovalRequired,
      isAchReversalDualApprovalRequired,
    });
  const hasReversePermission = isCollectPayment
    ? hasReverseCollectPermission
    : hasReverseSendPermission;
  const showReverseButton =
    !hideFooterActions &&
    institution?.features?.user_initiated_ach_reversals &&
    paymentActivity.isReversible &&
    hasReversePermission;
  const isPendingApproval = displayStatus === DisplayStatus.pending_approval;
  const isCompleted = displayStatus === DisplayStatus.completed;
  const isInProgress = displayStatus === DisplayStatus.in_progress;
  const isCanceled = displayStatus === DisplayStatus.canceled;

  const renderRow = (
    row: [string, string | undefined | ReactElement<any>],
    index: number,
    totalRows: number
  ) => {
    const [heading, content] = row;

    return content ? (
      <div
        key={`row-${heading}__${index}`}
        className={cc({ "margin--bottom--l": index !== totalRows - 1 })}
      >
        <Row>
          <Row.Item shrink>
            <p className="fontColor--secondary">{heading}</p>
          </Row.Item>
          <Row.Item>
            <div className={styles.rowContent}>
              <span className={styles.ellipsisWrapperRightAligned}>
                {content}
              </span>
            </div>
          </Row.Item>
        </Row>
      </div>
    ) : null;
  };

  const getTopCardPaymentScheduleDetails = () => {
    const oneTimeScheduled = recurrence === 1;
    const parentPayment = !parent && rruleModel && !oneTimeScheduled;

    const renderReversedPaymentLink = () => {
      if (isReversal || !reversal) return null;
      const recipientName =
        paymentActivity?.destinationDetails?.beneficiary_name ||
        recipient?.name ||
        "";
      const reversalLabel = l10n.getString("approval-drawer-reversal-title", {
        transactionType:
          getOriginalPaymentDirection(paymentActivity) === "collect"
            ? "debit"
            : "credit",
        recipientName,
      });

      return (
        <div className="margin--top--m">
          <h5 className={cc("margin--none", styles.drawerLabelHeading)}>
            {l10n.getString("label-reversed-payment")}
          </h5>
          <div>
            <Button
              kind="plain"
              size="s"
              onClick={() =>
                navigate(
                  `/${PAYMENT_ACTIVITY_ROUTE}/${reversal}${location.search}`
                )
              }
            >
              {reversalLabel}
              <span className="narmi-icon-chevron-right" />
            </Button>
          </div>
        </div>
      );
    };

    if (isReversal) {
      const recipientName =
        paymentActivity?.destinationDetails?.beneficiary_name ||
        recipient?.name ||
        "";
      const originalPaymentLabel =
        getOriginalPaymentDirection(paymentActivity) === "collect"
          ? l10n.getString("single-ach-payment-recipient-collect-from")
          : l10n.getString("single-ach-payment-recipient-send-to");

      return (
        <>
          {scheduledDate ? (
            <div className="margin--bottom--m">
              <h5 className={styles.drawerLabelHeading}>
                {isCanceled
                  ? l10n.getString("label-canceled-date")
                  : l10n.getString("label-scheduled-date")}
              </h5>
              <div>{Filters.longMonthDayYear(scheduledDate)}</div>
            </div>
          ) : null}
          {originalPayment ? (
            <>
              <h5 className={cc("margin--none", styles.drawerLabelHeading)}>
                {l10n.getString("label-original-payment")}
              </h5>
              <div>
                <Button
                  kind="plain"
                  size="s"
                  onClick={() =>
                    navigate(
                      `/${PAYMENT_ACTIVITY_ROUTE}/${originalPayment}${location.search}`
                    )
                  }
                  testId="view-original-payment-button"
                >
                  {`${originalPaymentLabel} ${recipientName}`}
                  <span className="narmi-icon-chevron-right" />
                </Button>
              </div>
            </>
          ) : null}
        </>
      );
    }

    // recurring "parent" payment
    if (parentPayment) {
      const { until } = rruleModel.options;

      const showCount = recurrence && recurrence < 100;
      let endString = l10n.getString("approval-drawer-value-indefinite");

      if (until) {
        endString = getUntilEndString(rruleModel);
      } else if (showCount) {
        endString = l10n.getString("approval-drawer-value-ends-count", {
          count: recurrence,
        });
      }

      const totalAmount = showCount
        ? getTotalAmount(recurrence, amountDollars)
        : "";

      const handleClickViewRecurring = () => {
        navigate({
          pathname: `/${PAYMENT_ACTIVITY_ROUTE}`,
          search: `?${
            FILTER_CONFIG[FilterChips.RECURRING_PAYMENT].queryKey
          }=${uuid}`,
        });
      };

      return (
        <>
          <h5 className={styles.drawerLabelHeading}>
            {l10n.getString("approval-drawer-label-payment-schedule")}
          </h5>
          {frequency && startDate ? (
            <div className="margin--bottom--m">
              {l10n.getString("approval-drawer-value-payment-schedule", {
                frequency,
                startDate: Filters.longMonthDayYear(startDate.toISOString()),
              })}
            </div>
          ) : null}
          <h5 className={styles.drawerLabelHeading}>
            {l10n.getString("approval-drawer-label-schedule-ends")}
          </h5>
          <div className={cc({ "margin--bottom--m": totalAmount })}>
            {endString}
          </div>
          {showCount ? (
            <>
              <h5 className={styles.drawerLabelHeading}>
                {l10n.getString("approval-drawer-label-payment-count", {
                  count: recurrence,
                })}
              </h5>
              <div>{totalAmount}</div>
            </>
          ) : null}
          {isInProgress || isCompleted || isCanceled ? (
            <div>
              <Button
                kind="plain"
                size="s"
                onClick={handleClickViewRecurring}
                testId="view-recurring-payments-button"
              >
                {l10n.getString("label-view-recurring")}
                <span className="narmi-icon-chevron-right" />
              </Button>
            </div>
          ) : null}
        </>
      );
    }

    // any 1 immediate payment that is pending approval
    if (isPendingApproval && !oneTimeScheduled) {
      return (
        <>
          <h5 className={styles.drawerLabelHeading}>
            {l10n.getString("label-scheduled-date")}
          </h5>
          <div>{l10n.getString("label-processes-approval")}</div>
        </>
      );
    }

    // child payment
    if (parent && !oneTimeScheduled) {
      const localizedFrequencies = {
        ONCE: l10n.getString("frequency-once"),
        DAILY: l10n.getString("frequency-daily"),
        WEEKLY: l10n.getString("frequency-weekly"),
        EVERY_TWO_WEEKS: l10n.getString("frequency-biweekly"),
        FIRST_AND_FIFTEENTH: l10n.getString("frequency-first-and-fifteenth"),
        MONTHLY: l10n.getString("frequency-monthly"),
        YEARLY: l10n.getString("frequency-yearly"),
      };
      const parentFrequency = generateFrequencyString(
        rruleModel,
        localizedFrequencies
      );
      const formattedStartDate = startDate
        ? Filters.shortMonthDayYear(startDate.toISOString())
        : "";

      const handleClickParent = () => {
        navigate(`/${PAYMENT_ACTIVITY_ROUTE}/${parent}`);
      };

      return (
        <>
          {scheduledDate ? (
            <div className="margin--bottom--m">
              <h5 className={styles.drawerLabelHeading}>
                {isCanceled
                  ? l10n.getString("label-canceled-date")
                  : l10n.getString("label-scheduled-date")}
              </h5>
              <div>{Filters.longMonthDayYear(scheduledDate)}</div>
            </div>
          ) : null}
          <h5 className={cc("margin--none", styles.drawerLabelHeading)}>
            {l10n.getString("label-part-recurring")}
          </h5>
          <div>
            <Button
              kind="plain"
              size="s"
              onClick={handleClickParent}
              testId="view-parent-recurring-payment-button"
            >
              {`${parentFrequency} payment starting on ${formattedStartDate}`}
              <span className="narmi-icon-chevron-right" />
            </Button>
          </div>
          {renderReversedPaymentLink()}
        </>
      );
    }

    // immediate payment or one time scheduled
    return (
      <>
        {scheduledDate ? (
          <>
            <h5 className={styles.drawerLabelHeading}>
              {isCanceled
                ? l10n.getString("label-canceled-date")
                : l10n.getString("label-scheduled-date")}
            </h5>
            <div>{Filters.longMonthDayYear(scheduledDate)}</div>
          </>
        ) : null}
        {renderReversedPaymentLink()}
      </>
    );
  };

  const TopCard = () => {
    const handleClickViewApprovals = () => {
      window.location.assign(
        `/approvals/pending?dualApprovalId=${dualApprovalId}`
      );
    };

    const handleClickBulkTitle = () => {
      navigate(
        `/${PAYMENT_ACTIVITY_ROUTE}/bulk/${paymentActivity.bulkPaymentId}`
      );
    };

    const handleClickWireImportBatch = () => {
      navigate(
        `/${PAYMENT_ACTIVITY_ROUTE}/file-import/${paymentActivity.wireImportBatchId}`
      );
    };

    const getNameDescription = () => {
      const destinationName =
        paymentActivity?.destinationDetails?.beneficiary_name;

      const getDirection = () =>
        isCollectPayment
          ? l10n.getString("collect-label")
          : l10n.getString("send-label");

      const getConnectorWord = () =>
        (isCollectPayment
          ? l10n.getString("label-from")
          : l10n.getString("label-to")
        ).toLowerCase();

      const getName = () => destinationName || recipient?.name || "";

      // "Reverse collect from John" "Reverse send to John"
      if (isReversal) {
        return l10n.getString("approval-drawer-reversal-title", {
          transactionType:
            getOriginalPaymentDirection(paymentActivity) === "collect"
              ? "debit"
              : "credit",
          recipientName: getName(),
        });
      }

      // "Send instant payment to John" "Collect instant payment from John"
      if (isInstantPayment) {
        return `${getDirection()} ${l10n.getString(
          "instant-payment-label"
        )} ${getConnectorWord()} ${getName()}`;
      }

      //  "Send same-day ACH payment to John" "Collect same-day ACH payment to John"
      if (isSameDayPayment) {
        return `${getDirection()} ${l10n.getString(
          "same-day-ach-payment-label"
        )} ${getConnectorWord()} ${getName()}`;
      }

      // "Domestic wire to John" "International wire to John""
      if (isFedWirePayment || isInternationalWirePayment) {
        const paymentType = isFedWirePayment
          ? l10n.getString("fedwire-label")
          : l10n.getString("international-wire-label");
        return `${paymentType} ${getConnectorWord()} ${getName()}`;
      }

      // "Send ACH payment to John" "Collect ACH payment from John"
      return `${getDirection()} ${l10n.getString(
        "ach-payment-card-title"
      )} ${getConnectorWord()} ${getName()}`;
    };

    const quote = paymentMetadata?.quote;
    const hasFxQuote = !!(quote && quote.converted_amount);
    const parsedTemplateName = paymentMetadata?.template_name
      ? parseTemplateName(paymentMetadata.template_name)
      : null;

    const getFxDisplayValues = () => {
      if (!quote) return null;
      const isUsdSpecified = quote.specified_currency === "USD";

      const foreignCurrency = isUsdSpecified
        ? quote.converted_currency
        : quote.specified_currency;
      const foreignAmount = isUsdSpecified
        ? quote.converted_amount || "0"
        : String(quote.amount);
      const usdAmount = isUsdSpecified
        ? String(quote.amount)
        : quote.converted_amount || "0";
      const foreignCurrencyCode = foreignCurrency.substring(0, 2);

      return {
        headlinePrefix: isUsdSpecified ? "~" : "",
        headlineAmount: utils.currencyFormattedValue(
          foreignAmount,
          foreignCurrency
        ),
        headlineCurrency: foreignCurrency,
        headlineFlag: countryCodeToFlag(foreignCurrencyCode),
        sendAmount: utils.currencyFormattedValue(usdAmount, "USD"),
        sendFlag: countryCodeToFlag("US"),
        sendLabel: isUsdSpecified
          ? l10n.getString("label-currency-amount-send-exactly")
          : l10n.getString("label-currency-amount-send-estimate"),
        exchangeRate: (() => {
          const parsedRate = parseFloat(quote.rate || "NaN");
          if (Number.isNaN(parsedRate)) return null;
          // Mimic useEffectiveConversionRate: invert when
          // converted_currency !== settlement_currency (USD)
          const effectiveRate =
            quote.converted_currency !== quote.settlement_currency
              ? 1 / parsedRate
              : parsedRate;
          return `1 USD = ${effectiveRate.toFixed(4)} ${foreignCurrency}`;
        })(),
      };
    };

    const fxDisplay = hasFxQuote ? getFxDisplayValues() : null;

    return (
      <div className="margin--top--l">
        <ContentCard kind="bordered">
          <h5 className="fontSize--m fontWeight--normal margin--bottom--xs">
            {getNameDescription()}
          </h5>
          {hasFxQuote && fxDisplay ? (
            <h1 className="fontColor--primary">
              <span
                className={cc({
                  [styles.strikeThrough]: showStrikeThrough,
                })}
              >
                {fxDisplay.headlinePrefix}
                {fxDisplay.headlineAmount}{" "}
                <span className="fontSize--m">
                  {fxDisplay.headlineFlag} {fxDisplay.headlineCurrency}
                </span>
              </span>
            </h1>
          ) : (
            <h1 className="fontColor--primary">
              <span
                className={cc({
                  [styles.strikeThrough]: showStrikeThrough,
                })}
              >
                {displayAmountDollars}
              </span>
            </h1>
          )}
          {hasFxQuote && fxDisplay && (
            <>
              <hr className="margin--bottom--l margin--top--l" />
              <Row>
                <Row.Item>
                  <h5 className={styles.drawerLabelHeading}>
                    {fxDisplay.sendLabel}
                  </h5>
                </Row.Item>
                <Row.Item shrink>
                  {fxDisplay.sendAmount} {fxDisplay.sendFlag} USD
                </Row.Item>
              </Row>
              {fxDisplay.exchangeRate && (
                <Row className="margin--top--s">
                  <Row.Item>
                    <h5 className={styles.drawerLabelHeading}>
                      {l10n.getString(
                        "international-wire-form-currency-estimated-exchange-rate"
                      )}
                    </h5>
                  </Row.Item>
                  <Row.Item shrink>{fxDisplay.exchangeRate}</Row.Item>
                </Row>
              )}
            </>
          )}
          <hr className="margin--bottom--l margin--top--l" />
          {getTopCardPaymentScheduleDetails()}
          {parsedTemplateName ? (
            <div className="margin--top--m">
              <h5 className={styles.drawerLabelHeading}>
                {l10n.getString("label-wire-template--template-name")}
              </h5>
              <div className={styles.templateNameRow}>
                <span className={styles.templateNameText}>
                  {parsedTemplateName.displayName}
                </span>
                {parsedTemplateName.isDeleted && (
                  <Chip
                    kind="warn"
                    label={l10n.getString("label-wire-template--deleted")}
                  />
                )}
              </div>
            </div>
          ) : null}
          {isPendingApproval && dualApprovalId && hasDualApprovalPermission ? (
            <Button
              kind="plain"
              size="s"
              onClick={handleClickViewApprovals}
              testId="view-payment-approvals-button"
            >
              {l10n.getString("label-view-approvals")}
              <span className="narmi-icon-chevron-right" />
            </Button>
          ) : null}
          {bulkTitle || wireImportBatchTitle ? (
            <>
              <h5
                className={cc(
                  "margin--top--m",
                  "margin--none",
                  styles.drawerLabelHeading
                )}
              >
                {bulkTitle ? l10n.getString("label-associated-bulk") : l10n.getString("label-associated-file-payment")}
              </h5>
              <Button
                kind="plain"
                size="s"
                onClick={bulkTitle ? handleClickBulkTitle : handleClickWireImportBatch}
                testId="view-bulk-payment-button"
              >
                {l10n.getString("label-view")} {bulkTitle || wireImportBatchTitle}
                <span className="narmi-icon-chevron-right" />
              </Button>
            </>
          ) : null}
        </ContentCard>
      </div>
    );
  };

  const PaymentDetailsCard = () => {
    if ((!paymentMetadata && !isReversal) || !paymentActivity?.model) {
      return null;
    }

    const config = drawerConfig[paymentActivity.model];
    if (!config) return null;

    const rows = config.paymentDetailsRows(
      paymentMetadata ?? {},
      institutionAccount,
      paymentActivity
    );

    return (
      <DrawerCard
        rows={rows.filter((row) => row[1])}
        title={l10n.getString("ach-payment-payment-details-title")}
      />
    );
  };

  const RecipientDetailsCard = () => {
    if (!paymentActivity?.model || (!paymentMetadata && !isReversal)) {
      return null;
    }

    const config = drawerConfig[paymentActivity.model];
    if (!config) return null;

    const rows = config.recipientDetailsRows(
      recipient,
      paymentActivity,
      paymentMetadata ?? {}
    );
    if (rows.length === 0) return null;
    const filteredRows = rows.filter((row) => row[1]);
    if (filteredRows.length === 0) return null;
    return (
      <div className="margin--top--xl">
        <Row>
          <Row.Item>
            <h5 className="fontColor--secondary fontWeight--bold margin--bottom--xs lineHeight--body-text">
              {l10n.getString("recipient-details").toUpperCase()}
            </h5>
          </Row.Item>
        </Row>
        <ContentCard kind="bordered">
          {filteredRows.map((row, index) =>
            renderRow(row, index, filteredRows.length)
          )}
        </ContentCard>
      </div>
    );
  };

  const renderDrawerContent = () => (
    <>
      <DrawerHeader
        title={l10n.getString("payment-activity-drawer-title")}
        status={displayStatus}
      />
      <TopCard />
      <PaymentDetailsCard />
      <RecipientDetailsCard />
    </>
  );

  return (
    <Drawer
      depth="524px"
      isOpen={isOpen}
      onUserDismiss={handleClose}
      paddingSize="xl"
      onNext={handleOnNext}
      onPrev={handleOnPrev}
      {...((showCancelButton || showReverseButton) && {
        footer: (
          <Row justifyContent="end">
            {showReverseButton && (
              <Row.Item shrink>
                <Button
                  kind="primary"
                  onClick={() =>
                    navigate(`${getReverseRoute(uuid)}${location.search}`)
                  }
                  label={l10n.getString("label-reverse-payment")}
                />
              </Row.Item>
            )}
            {showCancelButton && (
              <Row.Item shrink>
                <Button
                  kind="primary"
                  onClick={() => {
                    const model = paymentActivity?.model;
                    const isWire =
                      model === "WireTransaction" ||
                      model === "SwiftWireTransaction";
                    // wires and ACH reversals are cancelled via their dual
                    // approval, so pass the dual approval id, not the uuid
                    const usesDualApproval =
                      (isWire || isReversal) &&
                      Boolean(paymentActivity?.dualApprovalId);
                    handleToggleCancelDialog?.({
                      id: usesDualApproval
                        ? (paymentActivity?.dualApprovalId as string)
                        : (paymentActivity?.uuid as string),
                      model: model as string,
                      usesDualApproval,
                    });
                  }}
                  label={l10n.getString("label-cancel-payment")}
                />
              </Row.Item>
            )}
          </Row>
        ),
      })}
    >
      <LoadingSkeleton content="paragraph" showTitle isLoading={loading}>
        {renderDrawerContent()}
      </LoadingSkeleton>
    </Drawer>
  );
};

export default PaymentActivityDrawer;
